import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, Smartphone, Trash2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * 🆕 Feb 2026 — Impostazioni di sicurezza personali.
 *   - Toggle "Richiedi OTP al login" (opt-in per singolo utente)
 *   - Lista dispositivi "ricordami" attivi + revoca puntuale
 */
export default function MySecurityPrefs() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [devices, setDevices] = useState([]);
  const [revoking, setRevoking] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/security");
      setOtpEnabled(!!data?.otp_login_enabled);
      setDevices(Array.isArray(data?.trusted_devices) ? data.trusted_devices : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleOtp = async (v) => {
    setSaving(true);
    setOtpEnabled(v);
    try {
      await api.patch("/me/security", { otp_login_enabled: v });
      toast.success(v ? "OTP al login attivato" : "OTP al login disattivato");
    } catch (e) {
      setOtpEnabled(!v);
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const revoke = async (device_id) => {
    if (!window.confirm("Revocare questo dispositivo? Al prossimo accesso ti verrà chiesto nuovamente il codice OTP.")) return;
    setRevoking((r) => ({ ...r, [device_id]: true }));
    try {
      await api.delete(`/me/trusted-devices/${device_id}`);
      setDevices((d) => d.filter((x) => x.device_id !== device_id));
      toast.success("Dispositivo revocato");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setRevoking((r) => ({ ...r, [device_id]: false })); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-semibold">Sicurezza account</h1>
        </div>
        <p className="text-sm text-slate-500">Gestisci l&apos;autenticazione a due fattori e i dispositivi ricordati.</p>
      </div>

      <Card data-testid="security-otp-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            🔐 Autenticazione OTP via email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="flex-1">
              <p className="font-medium text-sm text-slate-800">Richiedi codice OTP al login</p>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                Quando attivo, dopo la password ti verrà inviato via email un codice di 6 cifre valido 5 minuti.
                I dispositivi che scegli di ricordare non richiederanno più il codice per 30 giorni.
              </p>
            </div>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            ) : (
              <Switch
                data-testid="security-otp-toggle"
                checked={otpEnabled}
                onCheckedChange={toggleOtp}
                disabled={saving}
              />
            )}
          </div>
          {otpEnabled && (
            <div className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2.5">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Protezione attiva. Se non ricevi il codice, controlla lo spam o verifica la configurazione SMTP con l&apos;amministratore.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="security-devices-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-5 w-5" /> Dispositivi ricordati (30 giorni)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carico…</p>
          ) : devices.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Nessun dispositivo ricordato al momento.</p>
          ) : (
            devices.map((d) => (
              <div key={d.device_id} data-testid={`trusted-device-${d.device_id}`} className="flex items-start justify-between gap-3 border border-slate-200 rounded-md p-3 bg-white">
                <div className="flex-1">
                  <p className="font-medium text-sm">{d.label || "Dispositivo"}</p>
                  <p className="text-[11px] text-slate-500 truncate" title={d.user_agent}>
                    {d.user_agent || "—"} · IP {d.ip || "—"}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Creato: <strong>{fmtDate(d.created_at)}</strong> · Ultimo accesso: <strong>{fmtDate(d.last_used_at)}</strong>
                  </p>
                  <p className="text-[11px] text-slate-500">Scade il <strong>{fmtDate(d.expires_at)}</strong></p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`revoke-device-${d.device_id}`}
                  onClick={() => revoke(d.device_id)}
                  disabled={!!revoking[d.device_id]}
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                >
                  {revoking[d.device_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  <span className="ml-1.5 text-xs">Revoca</span>
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
